/**
 * Shared OAuth popup flow for connector init / disconnect.
 *
 * Both apps (ari, ari-cloud) use the same popup + postMessage pattern.
 * They differ only in how they make authenticated requests, so the caller
 * provides an `authFetch` function with the same signature as `window.fetch`.
 */

/**
 * Structured connector-connect error codes. `timeout` and `access_denied`
 * (popup closed) are produced client-side; the rest come from the edge
 * function callback redirect (see _shared/auth/connect-errors.ts).
 */
export type ConnectionErrorCode =
  | 'admin_consent_required'
  | 'access_denied'
  | 'rate_limited'
  | 'connection_failed'
  | 'timeout';

export interface ConnectionResult {
  connected?: string;
  error?: ConnectionErrorCode | string;
  errorDescription?: string;
}

/**
 * Initiate OAuth connection in a centered popup.
 *
 * @param authFetch - Authenticated fetch function (same signature as window.fetch)
 * @param baseUrl - Auth backend base URL (e.g. from getAuthBackendUrl())
 * @param connectorId - Connector ID (primary key, e.g. "microsoft_copilot")
 */
export type AuthFetchFn = (url: string, init?: RequestInit) => Promise<Response>;

export async function initiateConnection(
  authFetch: AuthFetchFn,
  baseUrl: string,
  connectorId: string
): Promise<ConnectionResult> {
  const width = 520;
  const height = 700;
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;

  // Use a real page instead of about:blank to preserve browser session context
  // across the OAuth redirect chain (about:blank breaks cookie tracking in
  // modern browsers, causing AADSTS165000 errors with Microsoft login)
  const popup = window.open(
    '/oauth-loading.html',
    'oauth-connect',
    `width=${width},height=${height},left=${left},top=${top},popup=yes`
  );

  if (!popup) throw new Error('Popup blocked by browser');

  const returnTo = window.location.origin + window.location.pathname;

  try {
    const response = await authFetch(`${baseUrl}/auth/connectors/${connectorId}/connect`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ returnTo }),
    });

    if (!response.ok) {
      popup.close();
      throw new Error(`Failed to initiate connection: ${response.status}`);
    }

    const data = await response.json();

    popup.location.href = data.redirect_url || data.authorize_url;
  } catch (error) {
    popup.close();
    throw error;
  }

  return new Promise<ConnectionResult>((resolve) => {
    let resolved = false;

    const cleanup = () => {
      if (resolved) return;
      resolved = true;
      clearInterval(closedCheck);
      clearTimeout(timeout);
      window.removeEventListener('message', onMessage);
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'oauth-connect-result') return;
      cleanup();
      resolve({
        connected: event.data.connected,
        error: event.data.error,
        errorDescription: event.data.errorDescription,
      });
    };

    window.addEventListener('message', onMessage);

    const closedCheck = setInterval(() => {
      if (popup.closed) {
        cleanup();
        resolve({ error: 'access_denied' });
      }
    }, 300);

    const timeout = setTimeout(() => {
      cleanup();
      if (!popup.closed) popup.close();
      resolve({ error: 'timeout' });
    }, 300_000);
  });
}

/**
 * Disconnect a connector.
 *
 * @param authFetch - Authenticated fetch function
 * @param baseUrl - Auth backend base URL
 * @param connectorId - Connector ID to disconnect
 */
export async function disconnectProvider(
  authFetch: AuthFetchFn,
  baseUrl: string,
  connectorId: string
): Promise<boolean> {
  const response = await authFetch(`${baseUrl}/auth/connectors/${connectorId}`, {
    method: 'DELETE',
  });
  return response.ok;
}
